import { ReactNode, useCallback, useEffect, useRef } from "react";
import closeIcon from '../../assets/images/icons/close-icon.svg'
import { useTranslation } from "react-i18next";

const FOCUSABLE_SELECTORS = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([type="hidden"]):not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',')

type ModalProps = {
  isOpen: boolean,
  onClose: () => void
  children: ReactNode
  className?: string
  closeButtonOrder?: 'first' | 'last'
}

export function Modal({ isOpen, onClose, children, className, closeButtonOrder = 'first' }: ModalProps) {
  const { t } = useTranslation()
  const dialogRef = useRef<HTMLDivElement>(null)
  const lastActiveElementRef = useRef<HTMLElement | null>(null)

  const getFocusableElements = useCallback(() => {
    if (!dialogRef.current) return []
    return Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS))
      .filter((element) => {
        const ariaHidden = element.getAttribute('aria-hidden')
        const isAriaHidden = ariaHidden !== null && ariaHidden.toLowerCase() === 'true'
        return !element.hasAttribute('disabled') && !isAriaHidden
      })
  }, [])

  useEffect(() => {
    if (!isOpen) {
      return
    }
    lastActiveElementRef.current = document.activeElement as HTMLElement | null
    const focusFirst = () => {
      const focusableElements = getFocusableElements()
      if (focusableElements.length > 0) {
        focusableElements[0].focus()
        return
      }
      dialogRef.current?.focus()
    }
    const id = window.requestAnimationFrame(focusFirst)
    return () => {
      window.cancelAnimationFrame(id)
      lastActiveElementRef.current?.focus()
    }
  }, [getFocusableElements, isOpen])

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!isOpen) return
    if (event.key !== 'Tab') return
    const focusableElements = getFocusableElements()
    if (focusableElements.length === 0) {
      event.preventDefault()
      return
    }
    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]
    const activeElement = document.activeElement
    if (event.shiftKey) {
      if (activeElement === firstElement || activeElement === dialogRef.current) {
        event.preventDefault()
        lastElement.focus()
      }
      return
    }
    if (activeElement === lastElement) {
      event.preventDefault()
      firstElement.focus()
    }
  }, [getFocusableElements, isOpen])

  if (!isOpen) return null

  const closeButton = (
    <button
      onClick={onClose}
      className="absolute top-2 right-2 text-gray-600 hover:text-gray-800"
      aria-label={t("modal.close")}
    >
      <img src={closeIcon} alt="close" />
    </button>
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center mx-[40px]">
      <div className="fixed inset-0 bg-black opacity-50" onClick={onClose}></div>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        className={`relative bg-white z-10 rounded-[5px] ${className || ''}`}
      >
        {closeButtonOrder === 'first' && closeButton}
        {children}
        {closeButtonOrder === 'last' && closeButton}
      </div>
    </div>
  );
}
